import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, TreePine, Tent, MapPin, Euro, Clock, Users, ChevronLeft, ChevronRight, ExternalLink, Car, Phone } from 'lucide-react';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

// Types de logements au domaine
const LODGING_OPTIONS = [
  {
    id: 'chambre',
    name: 'Chambres au domaine',
    description: 'Chambres confortables avec lits doubles dans le domaine principal. Idéal pour les couples ou petites familles.',
    price: 50,
    capacity: '2-4 personnes',
    available: 10,
    image: '/uploads/chambre.png',
    icon: Home,
    features: ['Lit double', 'Salle de bain partagée', 'Petit-déjeuner inclus', 'Parking gratuit']
  },
  {
    id: 'cabane',
    name: 'Cabane dans les arbres',
    description: 'Une expérience unique et romantique perchée dans les arbres. Vue imprenable sur le domaine.',
    price: 35,
    capacity: '2 personnes',
    available: 1,
    image: '/uploads/cabane.png',
    icon: TreePine,
    features: ['Lit double', 'Terrasse privée', 'Vue panoramique', 'Accès par escalier']
  },
  {
    id: 'tente',
    name: 'Tentes cloche confort',
    description: 'Tentes spacieuses et tout confort avec vrais lits. L\'esprit camping sans les inconvénients !',
    price: 30,
    capacity: '2-4 personnes',
    available: 4,
    image: '/uploads/tente-cloche.png',
    icon: Tent,
    features: ['Lit double', 'Mobilier inclus', 'Éclairage', 'Sol en dur']
  }
];

// Hôtels partenaires
const PARTNER_HOTELS = [
  {
    name: 'B&B Hôtel',
    distance: '4 min en voiture (2,2 km)',
    address: '6 Parc Tertiaire Fenelon, 33370 Tresses',
    phone: '0 890 64 97 28',
    website: 'https://www.hotel-bb.com',
    priceRange: '60-80€/nuit',
    note: 'Réservation à faire par vos soins'
  }
];

const HebergementsPage = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % LODGING_OPTIONS.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + LODGING_OPTIONS.length) % LODGING_OPTIONS.length);
  };

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-display text-4xl md:text-5xl text-charcoal mb-4">
            Hébergement
          </h1>
          <p className="text-stone max-w-2xl mx-auto">
            Prolongez la fête en restant sur place ! Découvrez nos options d'hébergement 
            au Domaine de la Rose Blanche et nos hôtels partenaires.
          </p>
        </motion.div>

        {/* Section Domaine */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center">
              <Home className="w-5 h-5 text-terracotta" />
            </div>
            <h2 className="font-display text-2xl text-charcoal">Au Domaine de la Rose Blanche</h2>
          </div>

          {/* Carrousel principal */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="relative">
              <div className="aspect-[16/9] md:aspect-[21/9] overflow-hidden select-none">
                <img 
                  src={LODGING_OPTIONS[currentImageIndex].image}
                  alt={LODGING_OPTIONS[currentImageIndex].name}
                  className="w-full h-full object-cover pointer-events-none"
                  draggable="false"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="flex items-center gap-3 text-white mb-2">
                    {(() => {
                      const IconComponent = LODGING_OPTIONS[currentImageIndex].icon;
                      return <IconComponent className="w-6 h-6" />;
                    })()}
                    <h3 className="text-2xl md:text-3xl font-display">{LODGING_OPTIONS[currentImageIndex].name}</h3>
                  </div>
                  <p className="text-white/80 text-sm md:text-base max-w-xl">
                    {LODGING_OPTIONS[currentImageIndex].description}
                  </p>
                  <div className="flex flex-wrap gap-4 mt-4">
                    <span className="flex items-center gap-2 text-white bg-white/20 px-3 py-1 rounded-full text-sm">
                      <Euro className="w-4 h-4" />
                      {LODGING_OPTIONS[currentImageIndex].price}€/pers.
                    </span>
                    <span className="flex items-center gap-2 text-white bg-white/20 px-3 py-1 rounded-full text-sm">
                      <Users className="w-4 h-4" />
                      {LODGING_OPTIONS[currentImageIndex].capacity}
                    </span>
                    <span className="flex items-center gap-2 text-white bg-white/20 px-3 py-1 rounded-full text-sm">
                      {LODGING_OPTIONS[currentImageIndex].available} disponible{LODGING_OPTIONS[currentImageIndex].available > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Navigation */}
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); prevImage(); }}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white active:bg-white rounded-full p-2 md:p-3 shadow-lg transition-all z-10"
                aria-label="Image précédente"
              >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-charcoal" />
              </button>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); nextImage(); }}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white active:bg-white rounded-full p-2 md:p-3 shadow-lg transition-all z-10"
                aria-label="Image suivante"
              >
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-charcoal" />
              </button>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-3 py-4 bg-bone">
              {LODGING_OPTIONS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.preventDefault(); setCurrentImageIndex(idx); }}
                  className={`h-3 rounded-full transition-all ${
                    idx === currentImageIndex ? 'bg-terracotta w-8' : 'bg-stone-light hover:bg-stone w-3'
                  }`}
                  aria-label={`Voir ${LODGING_OPTIONS[idx].name}`}
                />
              ))}
            </div>
          </div>

          {/* Grille des options */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {LODGING_OPTIONS.map((option, idx) => (
              <motion.div
                key={option.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + idx * 0.1 }}
              >
                <Card 
                  className={`h-full cursor-pointer transition-all hover:shadow-lg ${
                    currentImageIndex === idx ? 'ring-2 ring-terracotta' : ''
                  }`}
                  onClick={() => setCurrentImageIndex(idx)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        currentImageIndex === idx ? 'bg-terracotta text-white' : 'bg-terracotta/10 text-terracotta'
                      }`}>
                        <option.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-medium text-charcoal">{option.name}</h3>
                        <p className="text-sm text-terracotta font-medium">{option.price}€/personne</p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {option.features.map((feature, i) => (
                        <li key={i} className="text-sm text-stone flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-terracotta rounded-full" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Option tente personnelle */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Tent className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-medium text-emerald-800 text-lg mb-2">Option gratuite : Votre propre tente</h3>
                <p className="text-emerald-700">
                  Vous pouvez planter votre propre toile de tente dans le jardin du domaine, <strong>sans frais</strong>.
                </p>
                <p className="text-sm text-emerald-600 mt-2">
                  Maximum 8 emplacements disponibles • Sanitaires à proximité
                </p>
              </div>
            </div>
          </div>

          {/* Infos pratiques */}
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-5 border border-stone-light">
              <Clock className="w-6 h-6 text-terracotta mb-3" />
              <h4 className="font-medium text-charcoal mb-1">Check-in / Check-out</h4>
              <p className="text-sm text-stone">Arrivée dès 14h le samedi</p>
              <p className="text-sm text-stone">Départ jusqu'au lundi 12h</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-stone-light">
              <Euro className="w-6 h-6 text-terracotta mb-3" />
              <h4 className="font-medium text-charcoal mb-1">Tarifs</h4>
              <p className="text-sm text-stone">Par personne pour le week-end</p>
              <p className="text-sm text-green-600 font-medium">Enfants gratuits</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-stone-light">
              <Users className="w-6 h-6 text-terracotta mb-3" />
              <h4 className="font-medium text-charcoal mb-1">Petit-déjeuner</h4>
              <p className="text-sm text-stone">Inclus pour tous les logements</p>
              <p className="text-sm text-stone">Dimanche matin</p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <p className="text-stone mb-4">Pour réserver votre hébergement, confirmez d'abord votre présence :</p>
            <Link to="/rsvp">
              <Button className="bg-terracotta hover:bg-terracotta/90 text-white px-8 py-6 text-lg rounded-full">
                Confirmer ma présence
              </Button>
            </Link>
            <p className="text-sm text-stone mt-3">
              Vous pourrez choisir votre hébergement lors de la confirmation
            </p>
          </div>
        </motion.section>

        {/* Section Hôtels */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="font-display text-2xl text-charcoal">Hôtels à proximité</h2>
          </div>

          <p className="text-stone mb-6">
            Si vous préférez un hébergement en dehors du domaine, voici nos partenaires :
          </p>

          <div className="grid md:grid-cols-1 gap-6 max-w-md mx-auto">
            {PARTNER_HOTELS.map((hotel, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-medium text-charcoal text-lg mb-3">{hotel.name}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2 text-stone">
                      <Car className="w-4 h-4 text-blue-500" />
                      {hotel.distance}
                    </p>
                    <p className="flex items-start gap-2 text-stone">
                      <MapPin className="w-4 h-4 text-blue-500 mt-0.5" />
                      {hotel.address}
                    </p>
                    <a href={hotel.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                      <ExternalLink className="w-4 h-4" />
                      hotel-bb.com
                    </a>
                    <a href={`tel:${hotel.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-stone hover:text-charcoal">
                      <Phone className="w-4 h-4 text-blue-500" />
                      {hotel.phone}
                    </a>
                    <p className="flex items-center gap-2 text-stone">
                      <Euro className="w-4 h-4 text-blue-500" />
                      {hotel.priceRange}
                    </p>
                    {hotel.note && (
                      <p className="text-amber-600 font-medium mt-2">
                        {hotel.note}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>
      </div>
    </PublicLayout>
  );
};

export default HebergementsPage;
